import type { EstimateTokens } from './estimate-cost';
import type { ParagraphEntry } from './indexParagraphs';
import { walkCast } from '../helpers/walkCast';
import type { CastTable, CastTableRow } from '../types';

/**
 * Returns the innermost table containing this paragraph (nearest table
 * ancestor), or null if the paragraph isn't in any table.
 */
export function findContainingTable(entry: ParagraphEntry): CastTable | null {
  for (let i = entry.ancestors.length - 1; i >= 0; i--) {
    const ancestor = entry.ancestors[i];
    if (ancestor.type === 'table') return ancestor;
  }
  return null;
}

/**
 * The row of `table` that directly contains this paragraph — the `tableRow`
 * ancestor immediately inside `table`. Undefined if the entry isn't inside the
 * given table.
 */
export function getRowOfEntry(entry: ParagraphEntry, table: CastTable): CastTableRow | undefined {
  const tableIndex = entry.ancestors.indexOf(table);
  if (tableIndex === -1) return undefined;
  const next = entry.ancestors[tableIndex + 1];
  return next?.type === 'tableRow' ? next : undefined;
}

/**
 * Selects which rows of a table to include around the matched rows, mirroring
 * the legacy `expandTableParagraphs`:
 *
 * - every matched row, always — free, never counts against the limit;
 * - the header row (the first row) if it fits the remaining limit (skipped, but
 *   expansion continues, when it doesn't);
 * - context rows alternating outward (up from the first matched row, down from
 *   the last), each only while it fits the remaining limit; the first context
 *   row that doesn't fit stops expansion entirely.
 *
 * Rows strictly between two matched rows are not auto-included (expansion grows
 * outward from the matched span, matching the legacy algorithm). Returns the
 * selected rows in priority order — matched rows first, then the header, then
 * the context rows in the order added — so a downstream global-budget pass can
 * truncate the lowest-priority rows first.
 */
export function expandTableRows(
  table: CastTable,
  matchedRows: Set<CastTableRow>,
  tableTokenLimit: number,
  estimateTokens: EstimateTokens
): CastTableRow[] {
  const rows = table.children;
  const matchedIndexes = rows
    .map((row, index) => (matchedRows.has(row) ? index : -1))
    .filter(index => index !== -1);
  if (matchedIndexes.length === 0) return [];

  const selected = new Set<number>(matchedIndexes);
  const order: number[] = [...matchedIndexes];
  let remaining = tableTokenLimit;

  const tryInclude = (index: number): boolean => {
    const cost = estimateTokens(rowText(rows[index]));
    if (cost > remaining) return false;
    selected.add(index);
    order.push(index);
    remaining -= cost;
    return true;
  };

  // Header row (the first row), unless it is already a matched row. If it
  // doesn't fit we keep going rather than stopping.
  if (rows.length > 0 && !selected.has(0)) tryInclude(0);

  // Context rows, alternating outward from the matched span.
  const firstMatched = matchedIndexes[0];
  const lastMatched = matchedIndexes[matchedIndexes.length - 1];
  let upOffset = 1;
  let downOffset = 1;
  let tryUp = true;

  while (true) {
    const upRow = firstMatched - upOffset;
    const downRow = lastMatched + downOffset;
    const hasUp = upRow >= 0 && !selected.has(upRow);
    const hasDown = downRow < rows.length && !selected.has(downRow);
    if (!hasUp && !hasDown) break;

    if (tryUp && hasUp) {
      if (!tryInclude(upRow)) break;
      upOffset += 1;
    } else if (tryUp) {
      upOffset += 1;
    }

    if (!tryUp && hasDown) {
      if (!tryInclude(downRow)) break;
      downOffset += 1;
    } else if (!tryUp) {
      downOffset += 1;
    }

    tryUp = !tryUp;
  }

  return order.map(index => rows[index]);
}

/**
 * Concatenated text of every run in a row (across its cells and any nested
 * tables), used to size a row against the table token limit. Mirrors the
 * legacy row-text measurement, which counted all segment text.
 */
function rowText(row: CastTableRow): string {
  let text = '';
  walkCast(row, node => {
    if (node.type === 'text') text += node.value;
  });
  return text;
}
